import { useResolvedTheme } from '../lib/theme';
import { useEffect, useState, type FormEvent } from 'react';
import type { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { api } from '../lib/api';
import { BILLING_COUNTRIES, profileProblem, taxIdType, TAX_ID_EXAMPLE, US_STATES } from '../../../src/billing/profile';

// F21 Payment Element (as ClauseBench): the customer pays inside Outlevo. The server creates the subscription or
// the top-up invoice and hands back a client secret; Stripe's iframe takes the card, so no card data reaches us.
// Stripe.js loads only when a payment starts.

export type Payment = { kind: 'payment'; client_secret: string; invoice: string; subscription?: string; amount_due: number; subtotal: number; tax: number; currency: string; plan?: string; credits?: number; ad_wallet_usd?: number };
export type Profile = { name: string | null; line1: string; line2: string; city: string; state: string; postal_code: string; country: string; tax_id: string | null };

const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

let stripePromise: Promise<Stripe | null> | null = null;
function loadStripeOnce(key: string) {
  stripePromise ??= import('@stripe/stripe-js').then((m) => m.loadStripe(key));
  return stripePromise;
}

// the card form follows the portal's theme
const appearance = (dark: boolean): StripeElementsOptions['appearance'] => ({
  theme: dark ? 'night' : 'stripe',
  variables: dark
    ? { colorPrimary: '#7fb0ff', colorText: '#e3e7ef', colorBackground: '#161a22', colorDanger: '#f08a7e', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', borderRadius: '12px', spacingUnit: '4px' }
    : { colorPrimary: '#1769e0', colorText: '#1c2230', colorDanger: '#b42318', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', borderRadius: '12px', spacingUnit: '4px' },
});

/** After paying (or coming back from a bank redirect): read Stripe now rather than waiting for the webhook. */
export async function confirmWithServer(ref: { invoice?: string; subscription?: string }) {
  return api<{ status: string | null }>('/portal/billing/confirm', { method: 'POST', body: ref });
}

/** How a payment ended: Stripe.js' PaymentIntent status, then what Stripe says of the invoice or subscription. */
export type Outcome = 'paid' | 'processing' | 'needs_action' | 'failed';
export function paymentOutcome(intent: string | null | undefined, server: string | null | undefined): Outcome {
  if (intent === 'requires_action' || intent === 'requires_confirmation') return 'needs_action';
  if (intent === 'requires_payment_method' || intent === 'canceled') return 'failed';
  // Stripe says the invoice is paid, or the subscription is active: the money is in
  if (server === 'paid' || server === 'active' || server === 'trialing') return 'paid';
  // a delayed method (bank debit) is "processing" with Stripe.js, and the invoice stays open until it settles
  if (intent === 'processing' || server === 'open' || server === 'incomplete' || server === 'past_due') return 'processing';
  return intent === 'succeeded' ? 'paid' : 'processing';
}
/** The sentence for an outcome; null for a paid one (the caller says what was bought). */
export const OUTCOME_TEXT: Record<Exclude<Outcome, 'paid'>, string> = {
  processing: 'Your payment is processing. Credits and plan changes appear when your bank confirms it; this page updates by itself.',
  needs_action: 'Your bank needs one more confirmation before the payment goes through. Open the payment again and finish it.',
  failed: 'The payment did not go through. Nothing was charged; try another card.',
};

export function PaymentPanel({ payment, publishableKey, title, onDone, onCancel }: { payment: Payment; publishableKey: string; title: string; onDone: (outcome: Outcome) => void; onCancel: () => void }) {
  const theme = useResolvedTheme();
  const [mod, setMod] = useState<null | typeof import('@stripe/react-stripe-js')>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all([import('@stripe/react-stripe-js'), loadStripeOnce(publishableKey)])
      .then(([m, s]) => { if (live) { setMod(m); setStripe(s); } })
      .catch(() => live && setErr('The payment form did not load. Check your connection and try again.'));
    return () => { live = false; };
  }, [publishableKey]);
  if (err) return <p className="error" role="alert">{err}</p>;
  if (!mod || !stripe) return <p className="muted" role="status">Loading the payment form…</p>;
  const { Elements } = mod;
  return (
    <section className="section pay-panel" aria-label={title}>
      <div className="section-head"><h2>{title}</h2><button type="button" className="link" onClick={onCancel}>Cancel</button></div>
      <dl className="pay-sum">
        <div><dt>Subtotal</dt><dd>{money(payment.subtotal)}</dd></div>
        <div><dt>Sales tax</dt><dd>{payment.tax ? money(payment.tax) : 'None for your address'}</dd></div>
        <div className="total"><dt>Due now</dt><dd>{money(payment.amount_due)}</dd></div>
      </dl>
      <Elements key={theme} stripe={stripe} options={{ clientSecret: payment.client_secret, appearance: appearance(theme === 'dark') }}>
        <PayForm mod={mod} payment={payment} onDone={onDone} />
      </Elements>
    </section>
  );
}

function PayForm({ mod, payment, onDone }: { mod: typeof import('@stripe/react-stripe-js'); payment: Payment; onDone: (outcome: Outcome) => void }) {
  const stripe = mod.useStripe();
  const elements = mod.useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr(null);
    const back = new URL('/billing', location.origin);
    back.searchParams.set('returned', '1');
    back.searchParams.set('invoice', payment.invoice);
    if (payment.subscription) back.searchParams.set('subscription', payment.subscription);
    // 3-D Secure opens in a modal on this page; only bank redirects leave it and come back to `back`
    const r = await stripe.confirmPayment({ elements, redirect: 'if_required', confirmParams: { return_url: back.toString() } });
    if (r.error) { setErr(r.error.message ?? 'The payment did not go through.'); setBusy(false); return; }
    const server = await confirmWithServer({ invoice: payment.invoice, subscription: payment.subscription }).catch(() => null);
    // not "paid" until Stripe says so: a delayed method, or a bank step still open, is its own message
    const outcome = paymentOutcome(r.paymentIntent?.status, server?.status);
    // a payment that needs another step or failed stays open with the reason; the card form is still there
    if (outcome === 'needs_action' || outcome === 'failed') { setErr(OUTCOME_TEXT[outcome]); setBusy(false); return; }
    onDone(outcome);
  };
  return (
    <form onSubmit={submit} className="pay-form">
      <mod.PaymentElement options={{ layout: 'tabs' }} />
      {err ? <p className="error" role="alert">{err}</p> : null}
      <button type="submit" className="btn btn-primary" disabled={!stripe || busy}>{busy ? 'Paying…' : `Pay ${money(payment.amount_due)}`}</button>
      <p className="small muted">Payments are processed by Stripe. Outlevo never sees your card details.</p>
    </form>
  );
}

/** Company details for invoices and sales tax; required once before the first payment. */
export function CompanyDetails({ profile, workspace, onSaved, onCancel }: { profile: Profile | null; workspace?: { name: string; billing_country: string } | null; onSaved: () => void; onCancel?: () => void }) {
  // a first visit starts from the workspace: its name and the billing country chosen when it was created
  const wsCountry = BILLING_COUNTRIES.some(([c]) => c === workspace?.billing_country) ? workspace!.billing_country : 'US';
  const [f, setF] = useState({ name: profile?.name ?? workspace?.name ?? '', line1: profile?.line1 ?? '', line2: profile?.line2 ?? '', city: profile?.city ?? '', state: profile?.state ?? '',
    postal_code: profile?.postal_code ?? '', country: profile?.country ?? wsCountry, tax_id: profile?.tax_id ?? '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [zipSeen, setZipSeen] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  // another country: its state means nothing there, and a tax ID is only kept where Stripe takes one
  const setCountry = (country: string) => setF({ ...f, country, state: '', tax_id: taxIdType(country) ? f.tax_id : '' });
  const save = async (e: FormEvent) => {
    e.preventDefault(); setErr(null);
    const problem = profileProblem(f);
    if (problem) { setErr(problem); setZipSeen(true); return; }
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([k, v]) => v !== '' || ['name', 'line1', 'city', 'postal_code', 'country'].includes(k)));
      await api('/portal/billing/profile', { method: 'PUT', body });
      onSaved();
    } catch (x) { setErr(readable(x as Error & { fix?: string })); } finally { setBusy(false); }
  };
  const us = f.country === 'US';
  const taxType = taxIdType(f.country);
  const zipBad = zipSeen && us && !/^\d{5}(-?\d{4})?$/.test(f.postal_code.trim());
  return (
    <form onSubmit={save} className="company-form" aria-label="Company details" noValidate>
      <p className="small muted">These appear on your invoices. Sales tax is calculated from the address.</p>
      <div className="field"><label htmlFor="bp-name">Company name</label><input id="bp-name" required autoComplete="organization" value={f.name} onChange={set('name')} /></div>
      <div className="field"><label htmlFor="bp-country">Country</label>
        <select id="bp-country" value={f.country} onChange={(e) => setCountry(e.target.value)} autoComplete="country">{BILLING_COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}</select></div>
      <div className="field"><label htmlFor="bp-line1">Address</label><input id="bp-line1" required autoComplete="address-line1" value={f.line1} onChange={set('line1')} /></div>
      <div className="field"><label htmlFor="bp-line2">Address line 2 <span className="muted">(optional)</span></label><input id="bp-line2" autoComplete="address-line2" value={f.line2} onChange={set('line2')} /></div>
      <div className="row3">
        <div className="field"><label htmlFor="bp-city">City</label><input id="bp-city" required autoComplete="address-level2" value={f.city} onChange={set('city')} /></div>
        <div className="field"><label htmlFor="bp-state">{us ? 'State' : 'State or region'}{us ? '' : <span className="muted"> (optional)</span>}</label>
          {us
            ? <select id="bp-state" required autoComplete="address-level1" value={f.state} onChange={set('state')}><option value="">Choose…</option>{US_STATES.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}</select>
            : <input id="bp-state" autoComplete="address-level1" value={f.state} onChange={set('state')} />}</div>
        <div className="field"><label htmlFor="bp-zip">{us ? 'ZIP code' : 'Postal code'}</label>
          <input id="bp-zip" required autoComplete="postal-code" inputMode={us ? 'numeric' : undefined} value={f.postal_code} onChange={set('postal_code')} onBlur={() => setZipSeen(true)} aria-invalid={zipBad || undefined} aria-describedby={zipBad ? 'bp-zip-err' : undefined} placeholder={us ? '78701' : ''} />
          {zipBad ? <span id="bp-zip-err" className="small error">5 digits, or 9 like 78701-1234</span> : null}</div>
      </div>
      {taxType ? <div className="field"><label htmlFor="bp-tax">VAT or tax ID <span className="muted">(optional)</span></label><input id="bp-tax" value={f.tax_id} onChange={set('tax_id')} placeholder={TAX_ID_EXAMPLE[f.country] ?? ''} /></div> : null}
      {err ? <p className="error" role="alert">{err}</p> : null}
      <div className="actions"><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save company details'}</button>
        {onCancel ? <button type="button" className="btn" onClick={onCancel}>Cancel</button> : null}</div>
    </form>
  );
}

/** The server's sentence with its fix; never a raw field path or a bare status. */
export function readable(e: Error & { fix?: string }) {
  const m = e.message.replace(/^[a-z_.]+: /, '');
  return e.fix && !m.includes(e.fix) ? `${m.replace(/\.?$/, '.')} ${e.fix}.` : m;
}
